import { isAxiosError } from 'axios';
import React, { useState } from 'react';

import { UserEntity } from 'Models/Entities';
import { transferUserEntitlement } from 'Services/Api/_HumanWritten/AnytimeTicketService';
import alertToast from 'Util/ToastifyUtils';
import { useCustomerSearch } from 'Views/Components/_HumanWritten/CustomerSelection/Queries/useCustomerSearch';
import { Flex } from 'Views/Components/Flex/Flex';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';
import { NewModal } from 'Views/Components/Modal/NewModal';
import { AsyncSelectInput } from 'Views/Components/New/AsyncSelectInput';
import { TextArea } from 'Views/Components/New/TextArea';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { UserEntitlementDto } from './UserEntitlementsUtils';
import { anytimeTicketInline, anytimeTicketLabel } from 'Util/_HumanWritten/anytimeTicketTerm';

const REASON_MAX_LENGTH = 100;

interface TransferEntitlementModalProps {
	entitlement: UserEntitlementDto;
	isOpen: boolean;
	onClose: () => void;
	onTransferred: () => void;
}

export function TransferEntitlementModal({
	entitlement,
	isOpen,
	onClose,
	onTransferred,
}: TransferEntitlementModalProps) {
	const { loadOptions } = useCustomerSearch();
	const [selectedCustomer, setSelectedCustomer] = useState<UserEntity | null>(null);
	const [reason, setReason] = useState('');
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [conditionsExpanded, setConditionsExpanded] = useState(false);

	const trimmedReason = reason.trim();
	const isValid = !!selectedCustomer && trimmedReason.length > 0;
	const isLocked = error !== null;

	const handleClose = () => {
		if (submitting) return;
		setSelectedCustomer(null);
		setReason('');
		setError(null);
		setConditionsExpanded(false);
		onClose();
	};

	const onConfirm = async () => {
		if (!isValid || submitting || isLocked) {
			return;
		}
		setSubmitting(true);
		try {
			await transferUserEntitlement(entitlement.id, selectedCustomer!.id, trimmedReason);
			alertToast(`${anytimeTicketLabel()} transferred`, 'success');
			onTransferred();
			handleClose();
		} catch (e) {
			const message =
				isAxiosError(e) && typeof e.response?.data === 'string' && e.response.data.length > 0
					? e.response.data
					: `This ${anytimeTicketInline()} cannot be transferred right now.`;
			setError(message);
			setSubmitting(false);
		}
	};

	return (
		<NewModal
			isOpen={isOpen}
			onRequestClose={handleClose}
			label={`Transfer ${anytimeTicketInline()}`}
			modalSize="sm"
		>
			<NewModal.Body>
				<Flex direction="col" gap="sm">
					<div data-test-id="transfer-entitlement-customer-select">
						<AsyncSelectInput
							label="Transfer to customer"
							required
							placeholder="Search by name, email or phone"
							loadOptions={loadOptions}
							defaultOptions
							cacheOptions
							isClearable
							isDisabled={isLocked}
							value={selectedCustomer}
							onChange={(option: any) => setSelectedCustomer((option as UserEntity) ?? null)}
							getOptionLabel={(option: any) => `${option.firstName} ${option.lastName} (${option.email})`}
							getOptionValue={(option: any) => option.id}
						/>
					</div>
					<div className="flex flex-col gap-1">
						<TextArea
							label="Reason"
							required
							placeholder="Reason for transfer"
							maxLength={REASON_MAX_LENGTH}
							disabled={isLocked}
							value={reason}
							onChange={e => setReason(e.target.value)}
							data-test-id="transfer-entitlement-reason"
						/>
						<p className="!text-xs text-slate-500">The customer will see this in their transfer email.</p>
					</div>
					{isLocked && (
						<MessageBox status="error" noBottomMargin icon="error_outline" title="Transfer not available">
							{error}
						</MessageBox>
					)}
				</Flex>
			</NewModal.Body>
			<NewModal.Footer>
				<div className="flex flex-col w-full gap-2">
					{!isLocked && (
						<Button
							display={Display.Solid}
							colors={Colors.Primary}
							newStyles
							onClick={onConfirm}
							disabled={!isValid || submitting}
							className="w-full"
							buttonProps={{ 'data-test-id': 'transfer-entitlement-confirm' } as any}
						>
							{submitting ? 'Transferring…' : 'Confirm transfer'}
						</Button>
					)}
					<Button
						display={Display.Outline}
						colors={Colors.Black}
						newStyles
						onClick={handleClose}
						disabled={submitting}
						className="w-full"
					>
						{isLocked ? 'Close' : 'Cancel'}
					</Button>
				</div>
			</NewModal.Footer>
		</NewModal>
	);
}
